import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getAssetLabel, truncateGuid } from '../utils/assetUtils';

export default function QuestEditDialog({ 
  open, 
  onClose, 
  quest, 
  assets = [],
  onSave 
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questOrder: 0,
    itemsToGive: [],
    rewards: [],
    isActive: 1,
    conversationIds: {
      startConversationId: null,
      endConversationId: null
    }
  });

  const [inputValues, setInputValues] = useState({});

  useEffect(() => {
    if (quest) {
      setFormData({
        title: quest.title || '',
        description: quest.description || '',
        questOrder: quest.questOrder || 0,
        itemsToGive: parseJSON(quest.itemsToGive),
        rewards: parseJSON(quest.rewards),
        isActive: quest.isActive ?? 1,
        conversationIds: quest.conversationIds || {
          startConversationId: null,
          endConversationId: null
        }
      });
    } else {
      // Reset for new quest
      setFormData({
        title: '',
        description: '',
        questOrder: 0,
        itemsToGive: [],
        rewards: [],
        isActive: 1,
        conversationIds: {
          startConversationId: null,
          endConversationId: null
        }
      });
    }
    setInputValues({});
  }, [quest, open]);

  const parseJSON = (field) => {
    try {
      if (typeof field === 'string') return JSON.parse(field);
      return field || [];
    } catch {
      return [];
    }
  };

  const filterOptions = useMemo(() => (options, state) => {
    const inputValue = state.inputValue;
    if (!inputValue || !inputValue.trim()) {
      return [];
    }
    const searchTerm = inputValue.trim().toLowerCase();
    const filtered = options.filter(option => {
      const title = (option.title || '').toLowerCase();
      const id = (option.id || '').toLowerCase();
      return title.includes(searchTerm) || id.includes(searchTerm);
    });
    return filtered;
  }, []);

  const handleAddReward = () => {
    setFormData(prev => ({
      ...prev,
      rewards: [...prev.rewards, { targetId: '', amount: 1 }]
    }));
  };

  const handleRemoveReward = (index) => {
    setFormData(prev => ({
      ...prev,
      rewards: prev.rewards.filter((_, i) => i !== index)
    }));
    const key = `reward-${index}`;
    setInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
  };

  const handleRewardChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      rewards: prev.rewards.map((reward, i) => 
        i === index ? { ...reward, [field]: value } : reward
      )
    }));
  };

  const handleAddItemToGive = () => {
    setFormData(prev => ({
      ...prev,
      itemsToGive: [...prev.itemsToGive, { targetId: '', amount: 1 }]
    }));
  };

  const handleRemoveItemToGive = (index) => {
    setFormData(prev => ({
      ...prev,
      itemsToGive: prev.itemsToGive.filter((_, i) => i !== index)
    }));
    const key = `item-${index}`;
    setInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
  };

  const handleItemToGiveChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      itemsToGive: prev.itemsToGive.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Quest</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          />
          
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />

          <TextField
            label="Quest Order"
            type="number"
            fullWidth
            value={formData.questOrder}
            onChange={(e) => setFormData(prev => ({ ...prev, questOrder: parseInt(e.target.value) || 0 }))}
          />

          {/* Items to Give */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Items Given at Start</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddItemToGive}>
                Add Item
              </Button>
            </Stack>
            <Stack spacing={1}>
              {formData.itemsToGive.map((item, index) => {
                const key = `item-${index}`;
                return (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                      options={assets}
                      getOptionLabel={(option) => getAssetLabel(option)}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      filterOptions={filterOptions}
                      openOnFocus={false}
                      inputValue={inputValues[key] || ''}
                      onInputChange={(e, newInputValue) => {
                        setInputValues(prev => ({ ...prev, [key]: newInputValue }));
                      }}
                      value={assets.find(a => a.id === item.targetId) || null}
                      onChange={(e, newValue) => {
                        handleItemToGiveChange(index, 'targetId', newValue?.id || '');
                        if (newValue) {
                          setInputValues(prev => ({ ...prev, [key]: '' }));
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2">
                              {getAssetLabel(option)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {truncateGuid(option.id)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} label="Asset" size="small" />}
                      sx={{ flexGrow: 1 }}
                    />
                    <TextField
                      label="Amount"
                      type="number"
                      size="small"
                      value={item.amount}
                      onChange={(e) => handleItemToGiveChange(index, 'amount', parseInt(e.target.value) || 1)}
                      sx={{ width: '120px' }}
                    />
                    <IconButton size="small" color="error" onClick={() => handleRemoveItemToGive(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {/* Rewards */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Rewards</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddReward}>
                Add Reward
              </Button>
            </Stack>
            <Stack spacing={1}>
              {formData.rewards.map((reward, index) => {
                const key = `reward-${index}`;
                return (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                      options={assets}
                      getOptionLabel={(option) => getAssetLabel(option)}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      filterOptions={filterOptions}
                      openOnFocus={false}
                      inputValue={inputValues[key] || ''}
                      onInputChange={(e, newInputValue) => {
                        setInputValues(prev => ({ ...prev, [key]: newInputValue }));
                      }}
                      value={assets.find(a => a.id === reward.targetId) || null}
                      onChange={(e, newValue) => {
                        handleRewardChange(index, 'targetId', newValue?.id || '');
                        if (newValue) {
                          setInputValues(prev => ({ ...prev, [key]: '' }));
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2">
                              {getAssetLabel(option)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {truncateGuid(option.id)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} label="Asset" size="small" />}
                      sx={{ flexGrow: 1 }}
                    />
                    <TextField
                      label="Amount"
                      type="number"
                      size="small"
                      value={reward.amount}
                      onChange={(e) => handleRewardChange(index, 'amount', parseInt(e.target.value) || 1)}
                      sx={{ width: '120px' }}
                    />
                    <IconButton size="small" color="error" onClick={() => handleRemoveReward(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {/* Conversation IDs */}
          <Box>
            <Typography variant="subtitle2" mb={1}>Conversation IDs</Typography>
            <Stack spacing={2}>
              <TextField
                label="Start Conversation ID"
                fullWidth
                value={formData.conversationIds?.startConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    startConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when quest starts"
              />
              <TextField
                label="End Conversation ID"
                fullWidth
                value={formData.conversationIds?.endConversationId || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conversationIds: {
                    ...prev.conversationIds,
                    endConversationId: e.target.value || null
                  }
                }))}
                placeholder="GUID for conversation shown when quest ends"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}
